// frontend/src/hooks/useMyCompatibleOrders.ts
import { useMemo } from 'react';
import type { P2POrder } from '../lib/api';
import { useMyOrders } from './useMyOrders';

export interface CompatibleOrderOption {
  order: P2POrder;
  remaining: bigint;
  /** target.premiumBps - mine.premiumBps. Positive = target worse than mine. */
  deltaBps: number;
  absDeltaBps: number;
}

/**
 * Filters the wallet's own orders to those that can validly cross the given
 * target row: opposite side, opposite chain, ACTIVE/PARTIAL, not expired,
 * has remaining capacity. Sorted by best delta for the initiator first
 * (smallest |delta|, then largest remaining capacity as tiebreaker).
 *
 * Returns [] when no wallet is connected or no compatible order exists.
 */
export function useMyCompatibleOrders(target: P2POrder): CompatibleOrderOption[] {
  const { data: myOrders } = useMyOrders();

  return useMemo(() => {
    if (!myOrders || myOrders.length === 0) return [];

    const now = Date.now();
    const oppositeSide =
      target.side === 'SELL_W2USDT' ? 'SELL_USDT' : 'SELL_W2USDT';

    const candidates = myOrders.filter((o) => {
      if (o.side !== oppositeSide) return false;
      if (o.chain === target.chain) return false;
      if (o.status !== 'ACTIVE' && o.status !== 'PARTIAL') return false;
      if (new Date(o.expiresAt).getTime() <= now) return false;
      if (o.isTake) return false;
      const remaining = BigInt(o.amount) - BigInt(o.filled);
      return remaining > 0n;
    });

    return candidates
      .map((o) => {
        const remaining = BigInt(o.amount) - BigInt(o.filled);
        const deltaBps = target.premiumBps - o.premiumBps;
        return {
          order: o,
          remaining,
          deltaBps,
          absDeltaBps: Math.abs(deltaBps),
        };
      })
      .sort((a, b) => {
        // Primary: smallest |delta|. Secondary: largest remaining capacity.
        if (a.absDeltaBps !== b.absDeltaBps) return a.absDeltaBps - b.absDeltaBps;
        if (a.remaining !== b.remaining) return a.remaining > b.remaining ? -1 : 1;
        return 0;
      });
  }, [myOrders, target]);
}
